import { useTranslation } from "react-i18next";
import type { ShortAnswerContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import type { QuestionRendererProps } from "./types";

/** Self-graded. The learner types a response (not auto-graded), then compares it
 *  to the model answer on reveal and marks themselves. */
export function ShortAnswerRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
  translateAnswers,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as ShortAnswerContent;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const enableAnswerGloss = enableGloss && !!translateAnswers;
  return (
    <div>
      <Prompt>{enableGloss ? <GlossableText text={c.question} context={c.question} /> : c.question}</Prompt>
      <textarea
        value={(answer as string) ?? ""}
        disabled={revealed}
        onChange={(e) => setAnswer(e.target.value)}
        rows={4}
        placeholder={t("Write your answer…")}
        className="mt-4 w-full resize-y rounded-xl border border-black/10 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:bg-black/[0.03]"
      />
      {revealed && (
        <div className="mt-4 rounded-xl bg-emerald-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{t("Model answer")}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-ink">
            {enableAnswerGloss ? (
              <GlossableText text={c.model_answer} context={c.question} />
            ) : (
              c.model_answer
            )}
          </p>
          {c.rubric_keywords?.length > 0 && (
            <p className="mt-2 text-xs text-emerald-800/70">
              {t("Look for:")}{" "}
              {enableAnswerGloss ? (
                <GlossableText text={c.rubric_keywords.join(", ")} context={c.question} />
              ) : (
                c.rubric_keywords.join(", ")
              )}
            </p>
          )}
        </div>
      )}
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
